import { useEffect, useState } from 'react';
import { Loader2 } from 'lucide-react';
import { fetchMotorwayLinkStatus, type MotorwayLinkStatusDto } from '../../services/api';

type Props = {
  lat: number;
  lng: number;
  enabled?: boolean;
};

export function MotorwayLinkStatusChip({ lat, lng, enabled = true }: Props) {
  const [status, setStatus] = useState<MotorwayLinkStatusDto | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!enabled || !Number.isFinite(lat) || !Number.isFinite(lng)) {
      setStatus(null);
      return;
    }
    let cancelled = false;
    setLoading(true);
    fetchMotorwayLinkStatus(lng, lat)
      .then((data) => {
        if (!cancelled) setStatus(data);
      })
      .catch(() => {
        if (!cancelled) setStatus(null);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [lat, lng, enabled]);

  if (!enabled) return null;
  if (loading) {
    return (
      <div className="flex items-center gap-2 text-xs text-gray-500 dark:text-neutral-400">
        <Loader2 className="h-3.5 w-3.5 animate-spin" />
        Checking link to main carriageway…
      </div>
    );
  }
  if (!status) return null;

  const tone =
    status.status === 'verified'
      ? 'border-emerald-300/80 bg-emerald-50 text-emerald-900 dark:border-emerald-800/60 dark:bg-emerald-950/30 dark:text-emerald-100'
      : status.status === 'unverified'
        ? 'border-amber-300/80 bg-amber-50 text-amber-950 dark:border-amber-800/60 dark:bg-amber-950/30 dark:text-amber-100'
        : 'border-gray-200 bg-gray-50 text-gray-800 dark:border-neutral-600 dark:bg-neutral-800 dark:text-neutral-200';

  const label =
    status.status === 'verified'
      ? 'Link verified'
      : status.status === 'unverified'
        ? 'Link unverified'
        : 'No link nearby';

  return (
    <div className={`rounded-lg border px-3 py-2 text-xs ${tone}`}>
      <p className="font-semibold">{label}</p>
      <p className="mt-0.5 opacity-90">{status.message}</p>
    </div>
  );
}
